/**
 * As decisões que a tela tira do manifesto — T21, T22, T24 e V5 da spec #408.
 *
 * Funções puras de propósito: o menu, a guarda de rota e a faixa de módulo precisam responder
 * as mesmas perguntas, e responder cada uma no seu componente foi como as 121 guardas frouxas
 * apareceram.
 */

import { CATALOGO_DE_TELAS } from '../config/menuConfig';
import {
  NUCLEO,
  type CoberturaDeModulo,
  type ContextoAtivo,
  type GrupoDoMenu,
  type ItemDoMenu,
  type Modulo,
} from '../types/manifesto';
import type { TelaDoCatalogo } from '../types/menuConfig';

/**
 * V5 — módulo desligado é leitura sempre, escrita e envio nunca. Imprimir e emitir documento
 * contam como leitura: o laudo que já existe continua saindo pela impressora.
 */
const ACOES_DE_LEITURA: readonly string[] = ['read', 'view', 'list', 'print', 'export', 'emit'];

/**
 * Qual módulo comercial cobre esta tela — o que o servidor publicou no item de menu.
 *
 * A tela não tem mapa próprio. Enquanto teve, eram dois mapas para a mesma pergunta (um por
 * família de permissão no servidor, um por item de menu aqui), e eles discordavam em 7 dos 44
 * itens — divergência que só aparecia para quem clicava. Item sem módulo é núcleo, que nenhum
 * contrato barra.
 */
export function moduloDoItem(item: Pick<ItemDoMenu, 'modulos'>): CoberturaDeModulo {
  const servem = item.modulos ?? [];
  return servem.length === 0 ? NUCLEO : servem;
}

/**
 * Os módulos que servem a esta tela. Quase toda tela é de um só; algumas servem a vários — a
 * emissão de documentos tira papel do que Segurança e Ambiental produzem, e não faz sentido
 * numa empresa que não contratou nenhum dos dois.
 */
function modulosQueServem(item: Pick<ItemDoMenu, 'modulos'>): readonly Modulo[] {
  return item.modulos ?? [];
}

/** T24 — empresa sem módulo enxerga só o núcleo. Fora da empresa `modulos` já vem cheio. */
export function moduloDisponivel(
  item: Pick<ItemDoMenu, 'modulos'>,
  modulos: readonly Modulo[]
): boolean {
  const servem = modulosQueServem(item);
  return servem.length === 0 || servem.some((modulo) => modulos.includes(modulo));
}

/**
 * O módulo que faltou para esta tela, ou nada quando ela está liberada. É o que a faixa de V5
 * precisa nomear — dizer "módulo encerrado" sem dizer qual não ajuda ninguém a ligar de volta.
 *
 * Servindo a vários, nomeia o primeiro: a faixa dá um caminho de volta, e listar todos os que
 * destravariam a tela transforma um aviso em decisão de compra.
 */
export function moduloEncerrado(
  item: Pick<ItemDoMenu, 'modulos'>,
  modulos: readonly Modulo[]
): Modulo | undefined {
  if (moduloDisponivel(item, modulos)) return undefined;
  return modulosQueServem(item)[0];
}

export function acaoLiberadaSemModulo(acao: string): boolean {
  // `view_pending` e `view_individual` são recortes de uma consulta, e a régua não separa o
  // que se lê inteiro do que se lê em pedaço.
  return ACOES_DE_LEITURA.includes(acao) || acao.startsWith('view_');
}

/** `/companies` casa `/companies/42/edit`, mas não `/companies-antigas`. */
function cobre(caminho: string, rota: string): boolean {
  return rota === caminho || rota.startsWith(`${caminho}/`);
}

/**
 * O item de menu que responde por uma rota. O caminho mais longo ganha: `/documents/emission`
 * é uma tela própria, e não um pedaço de `/documents`.
 */
export function itemDoCaminho(
  menu: readonly GrupoDoMenu[],
  rota: string
): ItemDoMenu | undefined {
  let achado: ItemDoMenu | undefined;

  for (const grupo of menu) {
    for (const item of grupo.itens) {
      if (!cobre(item.caminho, rota)) continue;
      if (!achado || item.caminho.length > achado.caminho.length) achado = item;
    }
  }

  return achado;
}

/**
 * A tela do catálogo que responde por uma rota. O manifesto traz só o que a pessoa alcança,
 * então é o catálogo que diz "esta rota **é** uma tela de menu" — e é a diferença entre as
 * duas listas que faz a guarda recusar em vez de deixar passar por omissão.
 *
 * Mesma regra de `itemDoCaminho`, sobre uma lista sem grupos: o catálogo não tem menu para
 * agrupar, porque o menu é do servidor.
 */
export function itemDoCatalogo(rota: string): TelaDoCatalogo | undefined {
  let achado: TelaDoCatalogo | undefined;

  for (const tela of CATALOGO_DE_TELAS) {
    if (!cobre(tela.caminho, rota)) continue;
    if (!achado || tela.caminho.length > achado.caminho.length) achado = tela;
  }

  return achado;
}

/** T4c — o texto que o backend grava em `UserMeta` sob `contexto_ativo`. */
export function serializarContexto(contexto: ContextoAtivo): string {
  if (contexto.tipo === 'plataforma') return 'plataforma';
  return `${contexto.tipo}:${contexto.id ?? 0}`;
}

export function mesmoContexto(um: ContextoAtivo, outro: ContextoAtivo): boolean {
  return um.tipo === outro.tipo && um.id === outro.id;
}

/** Como cada perspectiva se chama na tela. */
export const NOME_DA_PERSPECTIVA: Readonly<Record<ContextoAtivo['tipo'], string>> = {
  empresa: 'Empresa',
  prestador: 'Clínica',
  plataforma: 'Plataforma',
};

/** Como cada módulo comercial se chama para quem contrata. */
export const NOME_DO_MODULO: Readonly<Record<Modulo, string>> = {
  medicine: 'Medicina',
  safety: 'Segurança do Trabalho',
  environment: 'Ambiental',
  esocial: 'eSocial',
  psychosocial: 'Psicossocial',
};
